import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { DataGrid, trTR } from '@mui/x-data-grid';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getProductBasedExchangeRateForDynamic, getModelForDynamic, updateProductBasedExchangeRate, deleteProductBasedExchangeRate, addProductBasedExchangeRate } from 'src/store/slices/dynamicCatalogSlice';
import { getUser } from 'src/utils/Common';
import AlertAndLoading from 'src/components/AlertAndLoading';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  inputSx,
  lightButtonSx,
  listboxProps,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  limit: 20,
});

// Ürün yaşına göre oran alanları (kolon adı, API alanı, etiket)
const RATE_FIELDS = [
  { field: '0_12_Ay', api: 'Month_0_12_Rate', label: '0-1 Yıl' },
  { field: '13_24_Ay', api: 'Month_13_24_Rate', label: '1-2 Yıl' },
  { field: '25_36_Ay', api: 'Month_25_36_Rate', label: '2-3 Yıl' },
  { field: '37_60_Ay', api: 'Month_37_60_Rate', label: '3-5 Yıl' },
  { field: '61_72_Ay', api: 'Month_61_72_Rate', label: '5-6 Yıl' },
  { field: '73_84_Ay', api: 'Month_73_84_Rate', label: '6-7 Yıl' },
  { field: '85_120_Ay', api: 'Month_85_120_Rate', label: '7-10 Yıl' },
  { field: '121_180_Ay', api: 'Month_121_180_Rate', label: '10-15 Yıl' }
];

const emptyRates = () => RATE_FIELDS.reduce((acc, f) => ({ ...acc, [f.field]: '' }), {});
const isEmptyValue = (v) => v === null || v === undefined || String(v).trim() === '';
const gridLocale = { ...trTR.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: 'Kayıt bulunamadı.' };

function ProductBasedExchangeRateDynamic() {
  const classes = useDynamicStyles();
  const dispatch = useDispatch();
  const [productBasedExchangeRate, setProductBasedExchangeRate] = React.useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [modelsLoading, setModelsLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [modelAutoCompleteOptions, setModelAutoCompleteOptions] = React.useState([]);
  // Ekle / Düzenle popup
  const [editPopUp, setEditPopUp] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null); // null = yeni kayıt
  const [selectedModel, setSelectedModel] = React.useState(null);
  const [rates, setRates] = React.useState(emptyRates());
  const [showErrors, setShowErrors] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [responseMessage, setresponseMessage] = React.useState('');
  const [uyari, setUyari] = React.useState(false);
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });

  const isUpdate = !!editingRow;

  const loadRates = () => {
    setLoading(true);
    dispatch(getProductBasedExchangeRateForDynamic()).then(response => {
      if (response.meta?.requestStatus === 'fulfilled') {
        setProductBasedExchangeRate(Array.isArray(response.payload) ? response.payload : []);
      }
      setLoading(false);
    })
  };

  useEffect(() => {
    loadRates();
    setModelsLoading(true);
    dispatch(getModelForDynamic()).then(response => {
      if (response.payload?.length > 0) {
        setModelAutoCompleteOptions(response.payload.map(res => ({ ...res, label: res.ACIKLAMA, value: res.MODEL_KODU })));
      }
      setModelsLoading(false);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  function InfoAlert(ErrorDescription, ErrorType, ErrorTitle) {
    setresponseMessage(ErrorDescription);
    setAlertType({ ErrorType: ErrorType, ErrorTitle: ErrorTitle });
    setUyari(true);
  }

  // API cevabı: ErrorCode 0 başarılı
  const showApiResult = (payload) => {
    // eslint-disable-next-line eqeqeq
    const ok = payload?.ErrorCode == 0;
    InfoAlert(payload?.ErrorDescription || (ok ? 'İşlem başarılı.' : 'Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyiniz!'), ok ? 'success' : 'error', ok ? 'Başarılı' : 'Hata');
    return ok;
  };

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const existingModelCodes = useMemo(
    () => new Set(productBasedExchangeRate.map((r) => r.MODEL_KODU)),
    [productBasedExchangeRate]
  );

  const openAdd = () => {
    setEditingRow(null);
    setSelectedModel(null);
    setRates(emptyRates());
    setShowErrors(false);
    setEditPopUp(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setSelectedModel(row);
    setRates(RATE_FIELDS.reduce((acc, f) => ({ ...acc, [f.field]: row[f.field] ?? '' }), {}));
    setShowErrors(false);
    setEditPopUp(true);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditPopUp(false);
  };

  const saveRates = () => {
    const User = getUser().FirstName + ' ' + getUser().LastName;

    if (isUpdate) {
      const data = {
        NewModelCode: editingRow.MODEL_KODU,
        OldModelCode: editingRow.MODEL_KODU,
        User: User
      };
      RATE_FIELDS.forEach((f) => { data[f.api] = rates[f.field] || 0; });
      setSaving(true);
      dispatch(updateProductBasedExchangeRate(data))
        .then(response => {
          setSaving(false);
          if (showApiResult(response.payload)) {
            setProductBasedExchangeRate((prev) => prev.map((row) => {
              if (row.MODEL_KODU !== editingRow.MODEL_KODU) return row;
              const next = { ...row, IndirimiYapan: User };
              RATE_FIELDS.forEach((f) => { next[f.field] = rates[f.field] || 0; });
              return next;
            }));
            setEditPopUp(false);
          }
        }).catch(() => {
          setSaving(false);
          InfoAlert('Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyiniz!', 'error', 'Hata');
        });
      return;
    }

    // Yeni kayıt: model ve tüm oranlar zorunlu
    if (!selectedModel || RATE_FIELDS.some((f) => isEmptyValue(rates[f.field]))) {
      setShowErrors(true);
      return;
    }
    if (existingModelCodes.has(selectedModel.MODEL_KODU)) {
      InfoAlert('Bu model kodu zaten var', 'error', 'Hata');
      return;
    }
    const data = {
      NewModelCode: selectedModel.MODEL_KODU,
      OldModelCode: selectedModel.MODEL_KODU,
      User: User
    };
    RATE_FIELDS.forEach((f) => { data[f.api] = rates[f.field]; });
    setSaving(true);
    dispatch(addProductBasedExchangeRate(data))
      .then(response => {
        setSaving(false);
        if (showApiResult(response.payload)) {
          const newRow = {
            MODEL_KODU: selectedModel.MODEL_KODU,
            ACIKLAMA: selectedModel.ACIKLAMA,
            KUL_MODEL_KODU: selectedModel.KUL_MODEL_KODU,
            IndirimiYapan: User
          };
          RATE_FIELDS.forEach((f) => { newRow[f.field] = rates[f.field]; });
          setProductBasedExchangeRate((prev) => [...prev, newRow]);
          setEditPopUp(false);
        }
      }).catch(() => {
        setSaving(false);
        InfoAlert('Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyiniz!', 'error', 'Hata');
      });
  };

  const deleteRow = () => {
    if (!deleteTarget) return;
    const id = deleteTarget.MODEL_KODU;
    setSaving(true);
    dispatch(deleteProductBasedExchangeRate(id))
      .then(response => {
        setSaving(false);
        setDeleteTarget(null);
        if (showApiResult(response.payload)) {
          setProductBasedExchangeRate((prev) => prev.filter((row) => row.MODEL_KODU !== id));
        }
      }).catch(() => {
        setSaving(false);
        setDeleteTarget(null);
        InfoAlert('Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyiniz!', 'error', 'Hata');
      });
  };

  const filteredRows = useMemo(() => filterDynamicRows(productBasedExchangeRate, {
    searchText,
    searchFields: ['ACIKLAMA', 'KUL_MODEL_KODU']
  }), [productBasedExchangeRate, searchText]);

  const columns = [
    { field: 'KUL_MODEL_KODU', headerName: 'Model Kodu', width: 130 },
    { field: 'ACIKLAMA', headerName: 'Açıklama', flex: 1, minWidth: 200 },
    ...RATE_FIELDS.map((f) => ({
      field: f.field,
      headerName: f.label,
      width: 82,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ fontWeight: Number(p.value) ? 700 : 400, color: Number(p.value) ? DARK : '#b0bec5' }}>
          {p.value ?? '-'}
        </Box>
      )
    })),
    { field: 'IndirimiYapan', headerName: 'İndirimi Yapan', width: 150 },
    {
      field: 'actions', headerName: 'İşlem', width: 90, align: 'center', headerAlign: 'center',
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <>
          <Tooltip title="Düzenle">
            <IconButton size="small" onClick={() => openEdit(p.row)}>
              <EditIcon sx={{ fontSize: 18, color: ORANGE }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sil">
            <IconButton size="small" onClick={() => setDeleteTarget(p.row)}>
              <DeleteOutlineIcon sx={{ fontSize: 18, color: '#e53935' }} />
            </IconButton>
          </Tooltip>
        </>
      )
    }
  ];

  return (
    <>
      <AlertAndLoading Message={responseMessage} open={uyari} AlertType={alertType} onClose={uyariKapat} />
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Ürün Bazlı Değişim Oranları"
          subheader={loading ? 'Yükleniyor…' : `${productBasedExchangeRate.length} model · Ürün yaşına göre oranlar`}
          action={
            <Button startIcon={<AddIcon />} sx={darkButtonSx} onClick={openAdd}>
              Yeni Oran Ekle
            </Button>
          }
        />
        <DynamicListToolbar
          searchText={searchText}
          onSearchChange={setSearchText}
          placeholder="Model kodu veya açıklama ara"
          shownCount={filteredRows.length}
        />
        <DataGrid
          className={classes.grid}
          style={{ height: 560, width: '100%' }}
          rows={filteredRows}
          columns={columns}
          loading={loading}
          rowHeight={44}
          headerHeight={44}
          disableSelectionOnClick
          getRowId={(row) => row.MODEL_KODU}
          localeText={gridLocale}
        />
      </Card>

      {/* Ekle / Düzenle */}
      <Dialog open={editPopUp} onClose={closeEdit} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          {isUpdate ? 'Değişim Oranı Düzenle' : 'Yeni Ürün Bazlı Değişim Oranı'}
          <IconButton size="small" onClick={closeEdit} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          {isUpdate ? (
            <Box sx={{ p: '10px 14px', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
              <Box sx={{ fontSize: '14px', fontWeight: 700, color: '#263238' }}>{editingRow?.KUL_MODEL_KODU}</Box>
              <Box sx={{ fontSize: '12px', color: '#78909c' }}>{editingRow?.ACIKLAMA}</Box>
            </Box>
          ) : (
            <Autocomplete
              options={modelAutoCompleteOptions}
              filterOptions={filterOptions}
              size="small"
              fullWidth
              loading={modelsLoading}
              loadingText="Modeller yükleniyor…"
              noOptionsText="Model bulunamadı"
              ListboxProps={listboxProps}
              value={selectedModel}
              getOptionLabel={(o) => (o ? `${o.KUL_MODEL_KODU || ''} - ${o.ACIKLAMA || ''}` : '')}
              isOptionEqualToValue={(o, v) => o.MODEL_KODU === v.MODEL_KODU}
              getOptionDisabled={(o) => existingModelCodes.has(o.MODEL_KODU)}
              onChange={(event, newValue) => setSelectedModel(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model (kod veya açıklama ile arayın)"
                  sx={inputSx}
                  error={showErrors && !selectedModel}
                  helperText={showErrors && !selectedModel ? 'Model seçiniz' : 'Oranı tanımlı modeller seçilemez.'}
                  FormHelperTextProps={{ sx: { fontSize: '11px', mx: 0.5 } }}
                />
              )}
            />
          )}
          <Box className={classes.dialogSection} sx={{ mt: 2 }}>
            <div className={classes.sectionLabel}>Ürün yaşına göre oranlar</div>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              {RATE_FIELDS.map((f) => {
                const changed = isUpdate && String(editingRow?.[f.field] ?? '') !== String(rates[f.field] ?? '');
                const hasError = !isUpdate && showErrors && isEmptyValue(rates[f.field]);
                return (
                  <Grid item xs={6} sm={3} key={f.field}>
                    <TextField
                      label={f.label}
                      type="number"
                      size="small"
                      fullWidth
                      sx={{
                        ...inputSx,
                        '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], backgroundColor: changed ? '#fff8e1' : '#fff' }
                      }}
                      value={rates[f.field] ?? ''}
                      error={hasError}
                      onChange={(e) => setRates((prev) => ({ ...prev, [f.field]: e.target.value }))}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {isUpdate && (
              <Box sx={{ fontSize: '11px', color: '#78909c', mt: 1 }}>Değişen alanlar sarı ile gösterilir. Boş bırakılan alan 0 olarak kaydedilir.</Box>
            )}
            {!isUpdate && showErrors && RATE_FIELDS.some((f) => isEmptyValue(rates[f.field])) && (
              <Box sx={{ fontSize: '11px', color: '#d32f2f', mt: 1 }}>Tüm oran alanları doldurulmalıdır.</Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeEdit} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={saveRates}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            {isUpdate ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={() => !saving && setDeleteTarget(null)} fullWidth maxWidth="xs" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          Oran Sil
          <IconButton size="small" onClick={() => setDeleteTarget(null)} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: ORANGE, mt: '2px' }} />
            <Box sx={{ fontSize: '13px', color: DARK }}>
              <b>{deleteTarget?.KUL_MODEL_KODU}</b> modeline ait değişim oranları silinecek. Emin misiniz?
              <Box sx={{ mt: 0.5, fontSize: '12px', color: '#78909c' }}>{deleteTarget?.ACIKLAMA}</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={() => setDeleteTarget(null)} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 90, color: '#fff', backgroundColor: '#e53935', '&:hover': { backgroundColor: '#c62828' } }}
            onClick={deleteRow}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProductBasedExchangeRateDynamic;
